import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useWatch, useFormContext } from 'react-hook-form';

import intersection from 'lodash/intersection';
import isEqual from 'lodash/isEqual';
import { useDataProvider, Loading, Error } from 'react-admin';

import { getProductIds } from './utils';
import { getPlatformIds } from '../PlatformProductsSelect/utils';

import {
    SelectAllArrayInput,
    SELECT_ALL_VALUE,
    CLEAR_ALL_VALUE,
} from '@src/custom/SelectAllArrayInput';

/**
 * Product-specific states select component that filters states based on selected products and platforms.
 */
const ProductStatesSelect = ({ productsSource, platformsSource, source, isCreate, ...props }) => {
    const { setValue } = useFormContext();
    const value = useWatch({ name: source });
    const productsValue = useWatch({ name: productsSource });
    const platformsValue = useWatch({ name: platformsSource });

    const [states, setStates] = useState(null);
    const dataProvider = useDataProvider();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const ref = useRef();

    // Load states associated with selected products and platforms when they are changed
    useEffect(() => {
        const productIds = getProductIds(productsValue);
        const platformIds = getPlatformIds(platformsValue);

        const filterKey = { productIds, platformIds };

        if (isEqual(filterKey, ref.current)) return;

        ref.current = filterKey;

        const params = {
            pagination: { page: 1, perPage: 100 },
        };

        const filter = {};

        if (productIds) {
            filter.product_ids = productIds;
        }

        if (platformIds) {
            filter.platform_ids = platformIds;
        }

        if (Object.keys(filter).length > 0 && isCreate) {
            params.filter = filter;
        }

        dataProvider
            .getList('states', params)
            .then(({ data }) => {
                setStates(data);
                setLoading(false);
                setError(null);
            }).catch((error) => {
                setStates(null);
                setLoading(false);
                setError(error);
            });
    }, [productsValue, platformsValue, dataProvider, isCreate]);

    // Update selected states to include only states that are associated with selected products
    useEffect(() => {
        const safeValue = value || [];
        if (!states) return;
        if (!isCreate) return;
        if (safeValue.length === 0) return;

        // If 'select-all' or 'clear-all' is in value, wait for SelectAllArrayInput to handle it
        if (safeValue.includes(SELECT_ALL_VALUE) || safeValue.includes(CLEAR_ALL_VALUE)) {
            return;
        }

        const stateIds = states.map((s) => s.id);
        const filteredStates = intersection(safeValue, stateIds);

        // Only update if the filtered list is different
        const safeValueStrings = safeValue.map(String);
        const filteredStrings = filteredStates.map(String);

        if (!isEqual(safeValueStrings, filteredStrings)) {
            setValue(source, filteredStates, { shouldDirty: true, shouldValidate: true });
        }
    }, [states, value, isCreate, source, setValue]);

    if (loading) return <Loading />;
    if (error) return <Error error={error} />;

    return (
        <SelectAllArrayInput
            {...props}
            source={source}
            choices={states}
        />
    );
};

ProductStatesSelect.propTypes = {
    source: PropTypes.string.isRequired,
    productsSource: PropTypes.string.isRequired,
    platformsSource: PropTypes.string,
    isCreate: PropTypes.bool,
};

export default React.memo(ProductStatesSelect);
